import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Sparkles, ShoppingBag, Home, ArrowRight } from 'lucide-react';

const SuccessPage = () => {
  const { i18n } = useTranslation();
  const { state } = useLocation();
  const cashOnDelivery = state?.paymentMethod === 'cod';

  // Confetti particles
  const confettiColors = ['#22c55e', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6'];
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-32 overflow-hidden relative">
      {/* Animated background gradient */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          transition={{ duration: 1 }}
          className="absolute inset-0 bg-gradient-to-br from-green-500/10 via-transparent to-accent/10"
        />
        
        {/* Floating confetti */}
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-3 h-3 rounded-full"
            style={{ 
              backgroundColor: confettiColors[i % confettiColors.length],
              left: `${Math.random() * 100}%`,
            }}
            initial={{ 
              y: -20,
              x: 0,
              rotate: 0,
              opacity: 0 
            }}
            animate={{ 
              y: window.innerHeight + 20,
              x: Math.random() * 200 - 100,
              rotate: Math.random() * 720,
              opacity: [0, 1, 1, 0]
            }}
            transition={{ 
              duration: Math.random() * 3 + 3,
              delay: Math.random() * 2,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        ))}

        {/* Glowing orbs */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-64 h-64 bg-green-500/20 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ duration: 4, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-accent/20 rounded-full blur-3xl"
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ duration: 4, repeat: Infinity, delay: 2 }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 max-w-2xl mx-auto px-6 text-center"
      >
        {/* Success icon with ring animation */}
        <div className="relative inline-block mb-8">
          {/* Pulsing rings */}
          <motion.div
            className="absolute inset-0 rounded-full border-4 border-green-500/50"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1.5, opacity: 0 }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
          <motion.div
            className="absolute inset-0 rounded-full border-4 border-green-500/30"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 2, opacity: 0 }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
          />
          
          {/* Main icon */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ 
              delay: 0.2, 
              type: 'spring', 
              stiffness: 200,
              damping: 15
            }}
            className="relative"
          >
            <div className="w-28 h-28 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-lg shadow-green-500/30">
              <CheckCircle className="w-14 h-14 text-white" data-testid="success-icon" />
            </div>
            
            {/* Sparkle decorations */}
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="absolute -top-2 -right-2"
            >
              <Sparkles className="w-8 h-8 text-yellow-400" />
            </motion.div>
          </motion.div>
        </div>

        {/* Title with gradient */}
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="font-heading text-5xl md:text-6xl font-bold mb-4" 
          data-testid="success-title"
        >
          <span className="bg-gradient-to-r from-green-400 via-emerald-500 to-green-600 bg-clip-text text-transparent">
            {i18n.language === 'bg' ? 'Благодарим!' : 'Thank You!'}
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-2xl text-foreground/80 mb-2"
        >
          {i18n.language === 'bg' ? 'Поръчката е приета успешно!' : 'Order successfully received!'}
        </motion.p>

        {/* Description */}
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-lg text-muted-foreground mb-10 leading-relaxed max-w-md mx-auto"
        >
          {i18n.language === 'bg' 
            ? (cashOnDelivery ? 'Ще се свържем с вас за потвърждение. Плащате при получаване от куриера.' : 'Ще се свържем с вас скоро за потвърждение и детайли за плащане.')
            : (cashOnDelivery ? 'We will contact you to confirm your order. Pay the courier on delivery.' : 'We will contact you soon for confirmation and payment details.')
          }
        </motion.p>

        {/* Order confirmation box */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.7 }}
          className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6 mb-10"
        >
          <div className="flex items-center justify-center gap-3 text-green-500">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            >
              <Sparkles className="w-5 h-5" />
            </motion.div>
            <span className="font-medium">
              {i18n.language === 'bg' 
                ? 'Известие е изпратено до екипа ни' 
                : 'Notification sent to our team'}
            </span>
            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            >
              <Sparkles className="w-5 h-5" />
            </motion.div>
          </div>
        </motion.div>

        {/* Action buttons */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <Link
            to={`/${i18n.language}/shop`}
            data-testid="back-shop-button"
            className="group relative inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground font-bold uppercase tracking-wider px-8 py-4 overflow-hidden transition-all hover:shadow-lg hover:shadow-accent/25"
          >
            <span className="relative z-10 flex items-center gap-2">
              <ShoppingBag className="w-5 h-5" />
              {i18n.language === 'bg' ? 'Към магазина' : 'Back to Shop'}
            </span>
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-accent via-green-500 to-accent"
              initial={{ x: '-100%' }}
              whileHover={{ x: '100%' }}
              transition={{ duration: 0.5 }}
            />
          </Link>
          
          <Link
            to={`/${i18n.language}`}
            data-testid="back-home-button"
            className="group inline-flex items-center justify-center gap-2 border-2 border-border text-foreground font-medium px-8 py-4 hover:bg-secondary hover:border-accent/50 transition-all"
          >
            <Home className="w-5 h-5" />
            {i18n.language === 'bg' ? 'Начална страница' : 'Homepage'}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>

        {/* Footer note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-10 text-sm text-muted-foreground"
        >
          {i18n.language === 'bg' 
            ? '💚 Благодарим ви, че подкрепяте Фондация Национали \'94' 
            : '💚 Thank you for supporting Nationals \'94 Foundation'}
        </motion.p>
      </motion.div>
    </div>
  );
};

export default SuccessPage;
